<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import _ from 'lodash'
import { v4 } from 'uuid'

import Spinner from "@/components/ui/Spinner.vue"
import StreamService from '@/services/StreamService'
import StreamCardView from '@/components/ui/StreamCardView.vue'
import SubscriptionService from '@/services/SubscriptionService'
import IconBase from '@/components/icon/IconBase.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconClose from '@/components/icon/IconClose.vue'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import Alert from '../Atoms/Alert.vue'
import Badge from '../Badge.vue'
import { track, EVENTS } from '@/lib/tracking'

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		}
	},
	platformAdded: Boolean,
	platformDeleted: Boolean,
	platformCastrFiltered: null,
	platformUsageData: Object
})

const emit = defineEmits(['platform-saved', 'chosen-streams', 'dismiss'])

const store = useStore()

// State
const streams = ref([])
const loading = ref(false)
const platformUsage = ref(null)
const chosenStreams = ref([])
const dismissSecs = ref(10)
const dismissCountDown = ref(0)
const searchWidgetInput = ref('')

const categoryNameMaps = ref({
	live: 'livestreams',
	restream: 'Multistream',
	ipcam: 'IP Camera',
	vod: 'Video Hosting',
	transcoder: 'Transcoders',
})

const activeTab = ref('live') // Default to 'live' tab

// Computed
const addonSubscriptions = computed(() => store.getters['User/addonSubscriptions'])
const transcoderList = computed(() => store.getters['Transcoders/transcoders'])
const streamList = computed(() => store.getters['Streams/streams'])

const maxLiveStreams = computed(() => {
	return addonSubscriptions.value.find(subscription => subscription.category === 'live')?.definitionOverride?.streams || addonSubscriptions.value.find(subscription => subscription.category === 'live')?.package?.definition?.streams || 0
})

const maxTranscoderStreams = computed(() => {
	return addonSubscriptions.value.find(subscription => subscription.category === 'live')?.definitionOverride?.maxTranscoders || addonSubscriptions.value.find(subscription => subscription.category === 'live')?.package?.definition?.maxTranscoders || 0
})

const streamCategoryList = computed(() => {
	const cats = {
		'live': 1,    // All in one stream
		'transcoder': 2  // Transcoders
	}

	if (props.stream.type === 'subsecond') {
		// For subsecond, only show live streams
		return ['live']
	} else {
		// For other types, show both categories
		return ['live', 'transcoder']
	}
})

const streamFilter = computed(() => {
	return categoryName => {
		const filteredStreams = streams.value.filter(s => {
			if (props.stream._id === s._id) return false
			let bool = s.type === categoryName
			if (bool && searchWidgetInput.value) {
				const searchStr = searchWidgetInput.value.toLowerCase()
				bool = s.name.toLowerCase().includes(searchStr)
			}
			return bool
		})
		return filteredStreams
	}
})

const isLimitExceeded = computed(() => {
	if (platformUsage.value?.totalDestination) return platformUsage.value.usedDestination >= platformUsage.value.totalDestination

	if (!platformUsage.value?.uniqueDestination || !platformUsage.value?.extraDestination) {
		return false
	}

	return platformUsage.value.uniqueDestination.used >= platformUsage.value.uniqueDestination.limit ||
		platformUsage.value.extraDestination.used >= platformUsage.value.extraDestination.limit
})

// Methods
const getCategoryIcon = (categoryName) => {
	return require(`@/assets/icons/stream-type-${categoryName}-ico.svg`)
}

const toggleChoose = async (stream) => {
	if (isLimitExceeded.value && !chosenStreams.value.find(({ _id }) => _id === stream._id)) {
		return
	}

	const result = chosenStreams.value.find(({ _id }) => _id === stream._id)
	if (!result) {
		const addedStream = props.stream.platforms.find(({ streamObject }) => {
			if (streamObject) return streamObject._id === stream._id
		})
		if (addedStream) stream.added = true
		chosenStreams.value.push(stream)
	} else {
		const index = chosenStreams.value.indexOf(stream)
		chosenStreams.value = _.concat(
			chosenStreams.value.slice(0, index),
			chosenStreams.value.slice(index + 1)
		)
	}
}

const addWithinCast = async () => {
	if (chosenStreams.value.length === 0) return

	loading.value = true
	try {
		const toSave = chosenStreams.value.filter((video) => !video.added)
		if (toSave.length === 0) return

		const jobs = toSave.map((video) => {
			if (video.type === 'transcoder') {
				video.ingest = {
					address: `rtmp://${video.region.hostname}`,
					key: video.key
				}
				video.transcoder = true
			}
			const payload = {
				template: 'custom',
				server: video.ingest.address,
				key: video.ingest.key,
				region: video.region.name,
				enabled: false,
				castrName: video.name,
				castrStream: true,
				streamObject: video._id,
				transcoder: video.transcoder
			}

			if (video.config?.password) {
				payload.key += '?password=' + video.config.password
			}

			return StreamService.addStreamPlatform(props.stream._id, payload).then((savedPlatform) => ({
				video,
				savedPlatform: savedPlatform || payload
			}))
		})

		const results = await Promise.allSettled(jobs)
		const successes = []
		let anyFailure = false

		for (const result of results) {
			if (result.status === 'fulfilled') {
				const { video, savedPlatform } = result.value
				video.added = true
				successes.push(savedPlatform)
			} else {
				anyFailure = true
			}
		}

		if (anyFailure) {
			dismissCountDown.value = dismissSecs.value
		}

		if (successes.length > 0) {
			emit('chosen-streams', chosenStreams.value)
			for (const saved of successes) {
				track(EVENTS.DESTINATION_ADDED, { isCastrStream: true })
				emit('platform-saved', saved)
			}
		}
	} finally {
		loading.value = false
	}
}

const getStreamtype = () => {
	const category = props.stream.type
	let CategoryName = null
	if (category == 'live') {
		CategoryName = 'Livestreams'
	} else if (category == 'scheduled') {
		CategoryName = 'Pre-recorded Streams'
	} else if (category == 'restream') {
		CategoryName = 'Multistreams'
	} else if (category == 'transcoder') {
		CategoryName = 'Transcoders'
	}
	return CategoryName
}

const fetchPlatformUsage = async () => {
	// Use Vuex store for platform usage (prevents duplicates)
	const platformUsageData = await store.dispatch('PlatformUsage/getPlatformUsage', props.stream.type)
	if (platformUsageData) platformUsage.value = platformUsageData
}

const changeTab = (tabName) => {
	activeTab.value = tabName
}

const dismiss = () => {
	emit('dismiss')
}

const isChosen = (stream) => chosenStreams.value.find( streamFromArray => streamFromArray._id === stream._id)

// State styling for each row. The inner StreamCardView carries its own
// bg-surface-2, so we express selection as a ring frame + a tint applied to that
// inner figure (via the arbitrary `[&_figure]` variant) — that reads clearly
// instead of a background hidden behind the card.
const rowClass = (stream) => {
	if (stream.added) {
		// Already part of the stream: clearly "done" + not selectable.
		return 'ring-1 ring-success [&_figure]:!bg-success-transparent cursor-default'
	}
	if (isChosen(stream)) {
		// Picked in this session: strong brand frame + tint.
		return '!ring-2 ring-main [&_figure]:!bg-main-transparent'
	}
	if (!stream.enabled) {
		return 'ring-1 ring-transparent opacity-50 cursor-not-allowed'
	}
	return 'ring-1 ring-transparent hover:ring-surface-5'
}

// Watchers
watch(() => props.platformAdded, async () => {
	const platformUsageData = await StreamService.getPlatformUsage(props.stream.type)
	if (platformUsageData) platformUsage.value = platformUsageData
})

watch(() => props.platformDeleted, async () => {
	const platformUsageData = await StreamService.getPlatformUsage(props.stream.type)
	if (platformUsageData) platformUsage.value = platformUsageData

	try {
		const streamObj = await StreamService.getStream(props.stream._id)
		streams.value = streams.value.map((stream) => {
			const addedStream = streamObj.platforms.find(({ streamObject }) => {
				if (streamObject) return streamObject._id === stream._id
			})
			stream.added = addedStream ? true : false
			return stream
		})
	} catch (error) {
		console.log(error)
	}
})

// Lifecycle
onMounted(async () => {
	await store.dispatch('Transcoders/getTranscoders')
	await store.dispatch('Streams/getStreams')

	try {
		const streamsList = _.cloneDeep(streamList.value).filter(item => item.type === 'live')
		const transcoders = _.cloneDeep(transcoderList.value).map(item => ({
			...item,
			type: 'transcoder',
			platforms: [],
			sharedUsers: []
		}))

		setTimeout(() => {
			streams.value = _.cloneDeep(streamsList).concat(transcoders)
			loading.value = false

			streams.value.map((stream) => {
				const addedStream = props.stream.platforms.find(({ streamObject }) => {
					if (streamObject) return streamObject._id === stream._id
				})
				if (addedStream) stream.added = true
				return stream
			})
		}, 100)
	} catch (err) {
		console.error(err)
		loading.value = false
	}

	await fetchPlatformUsage()
})
</script>

<template>
	<div class="add-castr-stream-container">
		<div class="mb-6 flex justify-between items-center">
				<h3 class="add-platform-title">
					Add a stream within Castr
				</h3>
				<form-button type="link" isIcon @click="dismiss">
					<icon-base>
						<icon-close />
					</icon-base>
				</form-button>
		</div>

		<div class="usage-alerts">
			<!-- Case total destination -->
			<Alert type="normal" class="flex p-2 mb-3" v-if="platformUsage && platformUsage.totalDestination">
				<div>
					<span class="text-dark-9 text-s-m text-500 m-0">You have used {{ platformUsage.usedDestination }}/{{
						platformUsage.totalDestination }} destinations.</span>
					<span class="text-dark-7 text-s-s font-normal m-0"> Need more? <a class="text-underline text-dark-8"
							target="_blank" href="/app/subscribe">Upgrade</a> here.</span>
				</div>
			</Alert>

			<!-- Case unique/extra destination -->
			<Alert type="normal" class="flex p-2 mb-3"
				v-if="platformUsage && platformUsage.uniqueDestination && platformUsage.uniqueDestination.limit">
				<div>
					<span class="text-dark-9 text-s-m text-500 m-0">You have used {{ platformUsage.uniqueDestination.used }}/{{
						platformUsage.uniqueDestination.limit }} unique destinations and {{ platformUsage.extraDestination.used
						}}/{{
							platformUsage.extraDestination.limit }} extra ones<span v-if="getStreamtype()"> across all {{
							getStreamtype()
							}}</span>.</span>
					<span class="text-dark-7 text-s-s font-normal m-0"> Need more? <a class="text-underline text-dark-8"
							target="_blank" href="/app/subscribe">Upgrade</a> here.</span>
				</div>
			</Alert>

			<Alert type="error" text="Please upgrade your subscription to add more streams." class="flex items-center p-2 mb-3" v-if="isLimitExceeded" />
		</div>

		<div class="search-section">
			<div class="relative search-form order-0 md:order-1 mb-3 md:mb-0">
				<IconBase class="absolute z-10 left-2 text-dark-7 top-1/2 -translate-y-1/2">
					<IconSearch />
				</IconBase>
				<FormInput ref="searchWidgetInputElement" v-model="searchWidgetInput" placeholder="Search for streams"
					class="mb-4" customClasses="!bg-transparent !border-0 !pl-10 !pr-4" />
			</div>
		</div>

		<div class="streams-section">
			<div class="bg-surface-1 rounded-xl p-2">
				<!-- Tab Navigation -->
				<section class="flex text-tiny space-x-4 border-b border-surface-3 max-w-full mb-6 !px-2">
					<FormButton v-for="categoryName in streamCategoryList" :key="categoryName"
						:custom-classes="'capitalize hover:text-white hover:no-underline py-1 border-b-2 flex items-center text-nowrap ' + (activeTab === categoryName ? '!border-surface-9 text-white' : 'text-surface-8 border-transparent')"
						@click="changeTab(categoryName)">
						<img :src="getCategoryIcon(categoryName)" :alt="categoryName + 'streams'" class="mr-2 h-4 w-4">
						{{ categoryNameMaps[categoryName] || categoryName }}
						<Badge type="main" :id="'stream_count_' + categoryName" class="stream-count text-500 ml-2" pill>
							{{ streamFilter(categoryName).length }} / {{ categoryName === 'live' ? maxLiveStreams : maxTranscoderStreams }}
						</Badge>
					</FormButton>
				</section>

				<!-- Tab Content -->
				<ul class="flex flex-col gap-1.5 w-full">
					<li v-for="stream in streamFilter(activeTab)" :key="stream._id" >
						<FormButton class="w-full !p-0 rounded-lg ring-1 transition-all duration-150 overflow-hidden [&_figure]:!rounded-lg"
							:custom-classes="rowClass(stream)" @click="toggleChoose(stream)"
							:disabled="stream.added || !stream.enabled">
							<stream-card-view class="w-full" :new-tab-icon="false" :stream="stream" :compact-mode="true" hide-preview />
						</FormButton>
					</li>
				</ul>
			</div>
		</div>

		<div class="action-section">
			<FormButton :disabled="chosenStreams.length === 0 || isLimitExceeded" :isLoading="loading" type="main" size="md"
				full-width class="justify-center" @click="addWithinCast">
				Save
			</FormButton>
		</div>
	</div>
</template>

<style scoped>
.add-castr-stream-container {
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 1rem;
}

.header-section {
	margin-bottom: 1rem;
}

.add-platform-title {
	font-size: 20px;
	display: flex;
	align-items: center;
}

.usage-alerts {
	margin-bottom: 1rem;
}

.search-section {
	margin-bottom: 1rem;
}

.streams-section {
	flex: 1;
	margin-bottom: 1rem;
	max-height: calc(80vh - 200px);
	overflow-x: hidden;
	overflow-y: auto;
}

@media (max-width: 767px) {
	.streams-section {
		max-height: calc(70vh - 200px);
	}
}

.action-section {
	margin-top: auto;
}

.text-tiny {
	font-size: 0.875rem;
}

.space-x-4>*+* {
	margin-left: 1rem;
}

.border-surface-2 {
	border-color: var(--c-dark-3);
}

.border-surface-3 {
	border-color: var(--c-dark-4);
}

.border-surface-9 {
	border-color: var(--c-main-1);
}

.text-surface-8 {
	color: var(--c-dark-8);
}

.h-4 {
	height: 1rem;
}

.w-4 {
	width: 1rem;
}

:deep(.grid-item .castr-card) {
	margin-bottom: 0;
}

.search-form__toggle {
	z-index: 10;
}
:deep(figure h3) {
	@apply max-w-60;
}
</style>